import { catchError, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Warehouse } from '../_models';
import { environment } from '../../../environments/environment';
import { AppState } from '../_reducers';
import { selectAllWarehouses } from '../_selectors/warehouse.selectors';
import { TagService } from './tag.service';

@Injectable()
export class WarehouseService {
  private apiBaseUrl = environment.apiBaseUrl;
  warehouseList$: Observable<Warehouse[]>;
  warehouse: Warehouse;
  allWarehouses$: BehaviorSubject<Array<Warehouse>> = new BehaviorSubject<Array<Warehouse>>([]);
  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private tagService: TagService
  ) {
    this.warehouseList$ = this.store.pipe(select(selectAllWarehouses));
  }

  getFromLocalStore(): Observable<Warehouse[]> {
    return this.warehouseList$;
  }

  getWarehousesFromLocalStore(): Observable<Warehouse[]> {
    return this.warehouseList$;
  }

  getWarehouses(avoidTags: boolean = false): Observable<Warehouse[]> {
    const selectedInternalTagNames = avoidTags
      ? []
      : (this.tagService.selectedInternalTagNamesSubject?.getValue() ?? []);

    return this.http.get<Warehouse[]>(`${this.apiBaseUrl}api/private/v1/warehouses`).pipe(
      map((response) =>
        selectedInternalTagNames.length
          ? response.filter((w) => w.tags?.some((tag) => selectedInternalTagNames.includes(tag)))
          : response
      ),
      tap((warehouses) => this.allWarehouses$.next(warehouses))
    );
  }

  getWarehouse(id: number): Observable<Warehouse> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/warehouses/' + id)
      .pipe(map((response) => response));
  }

  getWarehouseById() {
    return this.warehouse;
  }

  setWarehouseById = (id: number): Warehouse => {
    return (this.warehouse = this.getLocalWarehouseById(id));
  };

  getLocalWarehouseById = (id: number): Warehouse => {
    const list = this.allWarehouses$.value;
    const item = list.find((i) => i.id === id);
    return item ? JSON.parse(JSON.stringify(item)) : null;
  };

  /**
   * Returns the container balance from the warehouse object.
   * This method avoids fetching the balance from the potentially large activity list for performance reasons.
   *
   * @returns {number} The container balance.
   */
  getContainerBalance = (warehouseId: number, containerId: number) => {
    const warehouseList = this.allWarehouses$.value;
    const warehouse = warehouseList.find((warehouse) => warehouse.id === warehouseId);

    if (!warehouse) {
      throw new Error(`Warehouse not found with id: ${warehouseId}`);
    }

    const container = warehouse.containers.find((container) => container.id === containerId);

    if (!container) {
      throw new Error(`Container not found with id: ${containerId}`);
    }

    return {
      balance: container.balance,
      unit: container.unit,
    };
  };

  saveWarehouse(warehouse: any): Observable<Warehouse> {
    // Creare una copia dell'oggetto per evitare errori di modifica dei riferimenti
    const warehouseCopy = { ...warehouse };

    delete warehouseCopy.lastUpdated;
    delete warehouseCopy.dateCreated;
    delete warehouseCopy.containers;

    const companySelected = JSON.parse(localStorage.getItem('companySelected')!);
    warehouseCopy.companyId = companySelected ? companySelected.id : null;

    if (warehouseCopy.id) {
      return this.http
        .put<Warehouse>(
          `${this.apiBaseUrl}api/private/v1/warehouses/${warehouseCopy.id}`,
          warehouseCopy
        )
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Warehouse>(`${this.apiBaseUrl}api/private/v1/warehouses/`, warehouseCopy)
        .pipe(map((response) => response));
    }
  }

  deleteWarehouse(id: number): Observable<Warehouse> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/warehouses/' + id).pipe(
      map(() => ({ id }) as Warehouse),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }
}
